import { useAsync } from "@react-hookz/web";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";

import { topic } from "shared/data/topic";

import { decodePlusDetailedListingQuery } from "scmp-app/components/plus/detailed-listing-popup/navigation";
import type { hooksAuthorAndTopicNameQuery } from "scmp-app/queries/__generated__/hooksAuthorAndTopicNameQuery.graphql";

export const useGetPageLocationForPlusPage = () => {
  const environment = useRelayEnvironment();
  const { asPath } = useRouter();

  const searchParameters = useSearchParams();
  const activePlusDetailedListing = decodePlusDetailedListingQuery(searchParameters);
  const isArticleInPath = asPath.includes("/article/");

  const [{ result }, { execute }] = useAsync(async () => {
    // article popup could be overlap on top of the detail listing page
    if (activePlusDetailedListing !== null) {
      switch (activePlusDetailedListing?.type) {
        case "author":
        case "topic":
        case "topic-without-related-contents": {
          const { entityUuid } = activePlusDetailedListing ?? {};
          switch (entityUuid) {
            case topic.openQuestionsSeries.entityUuid:
              return `${window.location.origin}/plus/open-questions`;
            default:
              break;
          }

          const type =
            activePlusDetailedListing.type === "topic-without-related-contents"
              ? "topic"
              : activePlusDetailedListing.type;
          const response = await fetchQuery<hooksAuthorAndTopicNameQuery>(
            environment,
            AuthorAndTopicNameQuery,
            {
              entityUuid,
              fetchAuthor: type === "author",
              fetchTopic: type === "topic",
            },
          ).toPromise();

          const entity = response?.[type]?.urlAlias;
          return entity ? `${window.location.origin}${entity}?display=plus` : window.location.href;
        }
        case "between-the-lines":
        case "daily-pulse":
        case "fact-sheet":
        case "featured-opinion":
        case "interactives":
        case "open-questions-series":
        case "quick-digest":
          return `${window.location.origin}/plus/${activePlusDetailedListing.type}`;
        case "china-future-tech-briefing":
          return `${window.location.origin}/plus/china-future-tech`;
        default:
          return window.location.href;
      }
    }
    if (isArticleInPath) return window.location.href;
  });

  useEffect(() => {
    void execute();
  }, [execute, asPath]);

  return result;
};

const AuthorAndTopicNameQuery = graphql`
  query hooksAuthorAndTopicNameQuery(
    $entityUuid: String!
    $fetchAuthor: Boolean!
    $fetchTopic: Boolean!
  ) {
    author(filter: { entityUuid: $entityUuid }) @include(if: $fetchAuthor) {
      urlAlias
    }
    topic(filter: { entityUuid: $entityUuid }) @include(if: $fetchTopic) {
      urlAlias
    }
  }
`;
